import { useMemo, useState } from "react";
import { mean, fmtN, fmtPct, fmtInt, C } from "./ivUtils";

const card = "mk-card p-5";
const RATING_COLORS = { Standard: C.green, "Non-Critical": C.yellow, Critical: C.red };
const BUCKETS = [["0–50", 0, 50], ["50–70", 50, 70], ["70–80", 70, 80], ["80–90", 80, 90], ["90–100", 90, 100.01]];

export default function IvQualityView({ data, slices, onOpenAgent }) {
  const [lobFilter, setLobFilter] = useState("");
  const [qslGrp, setQslGrp] = useState("");
  const pmt = slices.pmt;
  const qsl = slices.qsl;

  const ratingMix = useMemo(() => {
    const mix = { Standard: 0, "Non-Critical": 0, Critical: 0 };
    pmt.forEach((p) => { if (mix[p.rating] != null) mix[p.rating] += 1; });
    return mix;
  }, [pmt]);
  const pmtAvg = mean(pmt.map((p) => p.score).filter((v) => v != null));
  const lobs = useMemo(() => [...new Set(data.pmt.map((p) => p.lob))].sort(), [data]);
  const subRates = useMemo(() => {
    const rows = lobFilter ? pmt.filter((p) => p.lob === lobFilter) : pmt;
    return data.pmt_sub_names.map((name, i) => {
      const vals = rows.map((p) => p.subs[i]).filter((v) => v != null);
      return { name, rate: vals.length ? (100 * vals.reduce((a, b) => a + b, 0)) / vals.length : null, n: vals.length };
    }).sort((a, b) => (a.rate ?? 101) - (b.rate ?? 101));
  }, [pmt, lobFilter, data.pmt_sub_names]);

  const qslScores = qsl.map((q) => q.score);
  const qslAvg = mean(qslScores);
  const hist = BUCKETS.map(([label, lo, hi]) => ({ label, n: qslScores.filter((v) => v >= lo && v < hi).length }));
  const maxHist = Math.max(...hist.map((h) => h.n), 1);
  const wow = useMemo(() => {
    const by = {};
    qsl.forEach((q) => {
      const day = Number(q.d.slice(8, 10));
      const w = day <= 7 ? 1 : day <= 14 ? 2 : day <= 21 ? 3 : day <= 28 ? 4 : 5;
      (by[`${q.d.slice(0, 7)}-W${w}`] = by[`${q.d.slice(0, 7)}-W${w}`] || []).push(q.score);
    });
    return Object.keys(by).sort().map((k) => ({ k: k.slice(5), avg: mean(by[k]), n: by[k].length }));
  }, [qsl]);

  const grpOf = (c) => data.agents[c]?.tl || data.agents[c]?.team || "—";
  const grps = useMemo(() => [...new Set(qsl.map((q) => grpOf(q.c)))].filter((g) => g !== "—").sort(), [qsl]); // eslint-disable-line
  const logRows = useMemo(() => {
    let rows = qslGrp ? qsl.filter((q) => grpOf(q.c) === qslGrp) : qsl.filter((q) => q.score < 80);
    return [...rows].sort((a, b) => (a.d < b.d ? 1 : -1)).slice(0, 400);
  }, [qsl, qslGrp]); // eslint-disable-line

  const critRows = useMemo(() => Object.entries(data.critical)
    .map(([c, v]) => ({ c, name: data.agents[c]?.name || c, ...v }))
    .sort((a, b) => (b.fatal + b.below) - (a.fatal + a.below)), [data]);

  const roster = useMemo(() => Object.entries(data.roster)
    .map(([c, r]) => ({ c, name: data.agents[c]?.name || r.name || c, ...r }))
    .sort((a, b) => (a.adh ?? 101) - (b.adh ?? 101)), [data]);

  const totalRatings = Object.values(ratingMix).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-8">
      <div className="grid gap-5 lg:grid-cols-2">
        <div className={card} data-testid="iv-pmt-mix">
          <p className="font-mono-hud mb-4 text-[10px] uppercase tracking-[0.25em] text-thdim">PMT Rating Mix · {pmt.length} evaluations (voice LOBs only)</p>
          {pmt.length === 0 ? <p className="py-6 text-center text-sm text-thdim">No PMT evaluations loaded in the sheet yet — the PMT tab is currently empty. This section fills automatically once evaluations land.</p> : (
            <>
              <div className="mb-4 flex items-baseline gap-3">
                <p className="font-mono-hud text-3xl font-semibold" style={{ color: pmtAvg != null && pmtAvg >= data.targets.pmt ? C.green : C.red }}>{fmtPct(pmtAvg)}</p>
                <p className="text-xs text-thdim">average vs {data.targets.pmt}% target</p>
              </div>
              <div className="mb-3 flex h-3 w-full overflow-hidden rounded-full bg-thhover/10">
                {Object.entries(ratingMix).map(([k, v]) => <div key={k} style={{ width: `${(100 * v) / totalRatings}%`, background: RATING_COLORS[k] }} />)}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(ratingMix).map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-thhover/5 p-3">
                    <p className="text-xs" style={{ color: RATING_COLORS[k] }}>{k}</p>
                    <p className="font-mono-hud mt-1 text-lg font-semibold text-thstrong">{v}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className={card} data-testid="iv-qsl-dist">
          <p className="font-mono-hud mb-4 text-[10px] uppercase tracking-[0.25em] text-thdim">QSL Score Distribution · {qsl.length} audits · avg {fmtPct(qslAvg)}</p>
          <div className="space-y-2">
            {hist.map((h) => (
              <div key={h.label} className="flex items-center gap-3">
                <span className="w-14 font-mono-hud text-[10px] text-thdim">{h.label}</span>
                <div className="h-3 flex-1 rounded-sm bg-thhover/10">
                  <div className="h-3 rounded-sm" style={{ width: `${(100 * h.n) / maxHist}%`, background: C.yellow }} />
                </div>
                <span className="w-10 text-right font-mono-hud text-xs text-thbody">{h.n}</span>
              </div>
            ))}
          </div>
          <p className="font-mono-hud mt-4 mb-2 text-[10px] uppercase tracking-[0.15em] text-thdim">Week over week average</p>
          <div className="flex flex-wrap gap-2">
            {wow.map((w) => (
              <div key={w.k} className="rounded-lg bg-thhover/5 px-3 py-2">
                <p className="font-mono-hud text-[9px] uppercase text-thdim">{w.k}</p>
                <p className="font-mono-hud text-sm font-semibold" style={{ color: w.avg >= data.targets.qsl ? C.green : C.red }}>{fmtPct(w.avg)} <span className="text-[9px] text-thdim">n={w.n}</span></p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {pmt.length > 0 && (
        <div className={card} data-testid="iv-sub-params">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">PMT Sub-Parameter Pass Rates · weakest first</p>
            <select data-testid="iv-sub-lob-select" value={lobFilter} onChange={(e) => setLobFilter(e.target.value)}
              className="max-w-[220px] rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
              <option value="">All voice LOBs</option>
              {lobs.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="space-y-2.5">
            {subRates.map((s) => (
              <div key={s.name}>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs text-thbody">{s.name}</p>
                  <p className="font-mono-hud text-[10px] text-thmute">{fmtPct(s.rate)} · n={s.n}</p>
                </div>
                <div className="h-2.5 w-full rounded-full bg-thhover/10">
                  <div className="h-2.5 rounded-full" style={{ width: `${s.rate ?? 0}%`, background: s.rate == null ? C.grey : s.rate >= 80 ? C.green : s.rate >= 60 ? C.yellow : C.red }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className={card} data-testid="iv-critical-table">
          <p className="font-mono-hud mb-3 text-[10px] uppercase tracking-[0.25em] text-thdim">Critical Score · per agent (full month)</p>
          <div className="max-h-[360px] overflow-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-thcard"><tr className="border-b border-thborder/10">
                {["Agent", "Below Std", "Fatal", "Standard", "Critical %"].map((h, i) => (
                  <th key={h} className={`font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.12em] text-thdim ${i ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {critRows.map((r) => (
                  <tr key={r.c} onClick={() => onOpenAgent(r.c)} className="cursor-pointer border-b border-thborder/5 hover:bg-thhover/5">
                    <td className="max-w-[180px] truncate px-3 py-2 text-xs font-medium text-thstrong">{r.name}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-[#F59E0B]">{r.below || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-[#F43F5E]">{r.fatal || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.std || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={card} data-testid="iv-roster-table">
          <p className="font-mono-hud mb-1 text-[10px] uppercase tracking-[0.25em] text-thdim">India Adherence · live from roster codes</p>
          <p className="mb-3 text-xs text-thdim">Adherence = Present ÷ scheduled (P / P+SL+A+other) · Qatar values come from Monthly_Dump imports</p>
          <div className="max-h-[360px] overflow-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-thcard"><tr className="border-b border-thborder/10">
                {["Agent", "P", "WO", "SL", "A", "Adherence"].map((h, i) => (
                  <th key={h} className={`font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.12em] text-thdim ${i ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {roster.map((r) => (
                  <tr key={r.c} onClick={() => onOpenAgent(r.c)} className="cursor-pointer border-b border-thborder/5 hover:bg-thhover/5">
                    <td className="max-w-[180px] truncate px-3 py-2 text-xs font-medium text-thstrong">{r.name}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.P}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.WO}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.SL}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs text-[#F43F5E]">{r.A}</td>
                    <td className="px-3 py-2 text-right font-mono-hud text-xs font-semibold" style={{ color: r.adh == null ? undefined : r.adh >= 90 ? C.green : C.red }}>{fmtPct(r.adh)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={card} data-testid="iv-qsl-log">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">QSL Audit Log · score-only (no error taxonomy in this source)</p>
            <p className="mt-1 text-xs text-thdim">{qslGrp ? `Complete history for ${qslGrp}` : "Scores below 80 across all teams — pick a team for full history"}</p>
          </div>
          <select data-testid="iv-qsl-grp-select" value={qslGrp} onChange={(e) => setQslGrp(e.target.value)}
            className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
            <option value="">All teams (scores &lt; 80)</option>
            {grps.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-thcard"><tr className="border-b border-thborder/10">
              {["Date", "Agent", "Team/TL", "Score"].map((h) => (
                <th key={h} className="font-mono-hud whitespace-nowrap px-3 py-2 text-[9px] uppercase tracking-[0.12em] text-thdim">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {logRows.map((q, i) => (
                <tr key={i} data-testid={`iv-qsl-log-row-${i}`} onClick={() => onOpenAgent(q.c)} className="cursor-pointer border-b border-thborder/5 hover:bg-thhover/5">
                  <td className="whitespace-nowrap px-3 py-2 font-mono-hud text-xs text-thmute">{q.d}</td>
                  <td className="max-w-[180px] truncate px-3 py-2 text-xs font-medium text-thstrong">{q.agent || data.agents[q.c]?.name || q.c}</td>
                  <td className="max-w-[140px] truncate px-3 py-2 text-xs text-thmute">{data.agents[q.c]?.tl || data.agents[q.c]?.team || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs font-semibold" style={{ color: q.score >= 80 ? C.green : q.score > 0 ? C.yellow : C.red }}>{fmtInt(q.score)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logRows.length === 0 && <p className="py-8 text-center text-sm text-thdim">No audits match.</p>}
        </div>
      </div>
    </div>
  );
}
